/**
 * @fileoverview Consent-manager registry + login auto-grant.
 * @module consent/registry
 *
 * @description
 * Logging in counts as accepting tracking: an authenticated user has agreed to
 * the app's terms, so the auth helpers (`completeAuth`, `trackAuth`,
 * `pingActiveUser`) auto-accept consent instead of leaving a banner up — the
 * same behaviour a hand-rolled auth beacon would implement.
 *
 * Every {@link ConsentManager} registers itself here on construction (unless
 * created with `grantOnAuth: false`), so the grant reaches whichever manager(s)
 * the app wired — `setupConsent`, `attachConsentBanner`, `renderConsentBanner`
 * or a bare `new ConsentManager()` — without the SDK knowing cookie names.
 */

import type { ConsentManager } from './manager';
import { FlowGridTransport } from '../client/transport';

const managers = new Set<ConsentManager>();

/** Register a manager for the login auto-grant. Called from the constructor. */
export function registerConsentManager(manager: ConsentManager): void {
  managers.add(manager);
}

/**
 * Accept all consent categories because the visitor authenticated.
 *
 * Fired automatically by the auth helpers ({@link FlowGrid.completeAuth},
 * {@link FlowGrid.trackAuth}, {@link FlowGrid.pingActiveUser}); call it
 * yourself only from a fully custom auth flow that bypasses them.
 *
 * Semantics mirror clicking "Accept all" on every registered manager: the
 * choice is persisted in each manager's cookie (so banners gated on
 * `hasConsented()` stay hidden on future visits), each manager's `onChange`
 * fires (starting consent-gated streams like session replay), and it
 * supersedes an earlier anonymous decline — logging in wins. Managers created
 * with `grantOnAuth: false` are left untouched. Already fully-granted managers
 * are skipped so repeated auth pings don't rewrite cookies.
 *
 * With no manager registered, it just opens the transport-level gates, which
 * covers apps that called `FlowGridTransport.setConsent` by hand.
 */
export function grantConsentOnAuth(): void {
  if (managers.size === 0) {
    FlowGridTransport.setConsent({ analytics: true, marketing: true });
    return;
  }
  for (const manager of managers) {
    try {
      const prefs = manager.getPreferences();
      if (!manager.hasConsented() || !prefs.analytics || !prefs.marketing) {
        manager.acceptAll();
      }
    } catch {
      /* consent stamping is best-effort — never let it break auth tracking */
    }
  }
}
